/**
 * SOPStepEditor — two-panel step editor: step list on the left, the selected
 * step's full editor (rich-text description) on the right.
 *
 * Design notes:
 *  - `draft` only holds keys whose value DIFFERS from the currently selected
 *    step (deep-equal check). That prevents "unsaved" flags when the user
 *    merely clicks around without editing anything.
 *  - Each step in the list has a "+" button to insert a NEW step right below
 *    it (uses the `?insert_after=` backend query param).
 *  - New "Advanced" collapsible panel lets step authors set:
 *      • Depends on step  — this step is blocked until the chosen step is done
 *      • Wait N days      — additional cooldown after the dependency completes
 *        (used for 30-day / 60-day / 90-day review scenarios)
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import SmartRichText from "@/components/SmartRichText";
import SOPAIGenerator from "@/components/sops/SOPAIGenerator";
import RoutePicker from "@/components/RoutePicker";
import {
  Trash2, Plus, ChevronUp, ChevronDown, ListChecks, FileText, Upload, Compass,
  Film, MonitorPlay, Sparkles, PlusSquare, Clock, Link2, ExternalLink, ChevronRight,
} from "lucide-react";

const KIND_TIPS = {
  checkbox: [
    "Best for simple confirmations: 'Confirm signed contract received', 'Uploaded to CRM'.",
    "Skip long descriptions — the checkbox itself IS the action.",
  ],
  form: [
    "Use for structured data capture — client name, invoice number, feedback rating.",
    "Add validation hints in the Instructions field so the runner knows what 'good' looks like.",
  ],
  upload: [
    "Ask for the exact deliverable — 'Upload signed SOW (PDF)'.",
    "Combine with an 'Advanced → Depends on step' to unlock only after prerequisites.",
  ],
  navigate: [
    "Perfect for guided onboarding — send the runner directly to a specific screen.",
    "Use the picker's search box; the tree is grouped by module for quick discovery.",
  ],
  external_url: [
    "Great for third-party portals — bank, CRM, WhatsApp Business.",
    "Toggle auto-open on if you want the URL to pop the moment the step is shown.",
    "Toggle it off for reference links you'd rather the runner explicitly click.",
  ],
  video: [
    "Paste any Loom / YouTube / Vimeo URL. The runner sees an inline embed.",
    "Keep videos < 3 minutes — record a fresh clip rather than linking a 30-min recording.",
  ],
  embed: [
    "Any URL that supports iframe embedding — Google Sheets, Slides, Notion pages.",
    "Ensure the linked doc's sharing is set to 'Anyone with link' or the runner will see a permission error.",
  ],
};

const KINDS = [
  { v: "checkbox", label: "Checkbox", icon: ListChecks },
  { v: "form", label: "Form entry", icon: FileText },
  { v: "upload", label: "File upload", icon: Upload },
  { v: "navigate", label: "Navigate in-app", icon: Compass },
  { v: "external_url", label: "Open external URL", icon: ExternalLink },
  { v: "video", label: "Video / walkthrough", icon: Film },
  { v: "embed", label: "Embed (Sheets / Slides / any URL)", icon: MonitorPlay },
];

export const kindIcon = (kind) => (KINDS.find((k) => k.v === kind) || KINDS[0]).icon;

const equal = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

export default function SOPStepEditor({ sopId, sopTitle }) {
  const { activeWorkspaceId } = useAuth();
  const [steps, setSteps] = useState([]);
  const [busy, setBusy] = useState(false);
  const [selId, setSelId] = useState(null);
  const [draft, setDraft] = useState({});
  const [showAI, setShowAI] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const load = async (keepSel = true) => {
    try {
      const { data } = await api.get(`/sops/${sopId}/steps`);
      setSteps(data || []);
      setDraft({});
      if (!keepSel || !data?.some((s) => s.id === selId)) setSelId(data?.[0]?.id || null);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to load steps"));
    }
  };
  useEffect(() => { if (sopId) load(false); }, [sopId]);  // eslint-disable-line

  // Whenever selection changes, reset draft (unconditional — safe because
  // selectStep guards navigation when dirty).
  useEffect(() => { setDraft({}); }, [selId]);

  const selected = steps.find((s) => s.id === selId);
  const dirty = Object.keys(draft).length > 0;

  const selectStep = (id) => {
    if (id === selId) { setSelId(null); return; }  // toggle collapse
    if (dirty && !confirm("Discard unsaved changes to the current step?")) return;
    setSelId(id);
  };

  const addStep = async (opts = {}) => {
    setBusy(true);
    try {
      const params = opts.insertAfter ? { insert_after: opts.insertAfter } : {};
      const { data } = await api.post(`/sops/${sopId}/steps`, {
        kind: "checkbox", title: "New step", is_required: true, order: 0,
      }, { params });
      await load();
      setSelId(data.id);
      setDraft({});
    } catch (e) { toast.error(apiErrorMessage(e, "Add failed")); }
    finally { setBusy(false); }
  };

  const saveStep = async () => {
    if (!selected || !dirty) return;
    try {
      await api.patch(`/sop-steps/${selected.id}`, draft);
      await load();
      toast.success("Step saved");
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
  };

  const delStep = async (stepId) => {
    if (!confirm("Delete this step?")) return;
    try {
      await api.delete(`/sop-steps/${stepId}`);
      if (stepId === selId) { setSelId(null); setDraft({}); }
      await load();
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };

  const move = async (idx, dir) => {
    const to = idx + dir;
    if (to < 0 || to >= steps.length) return;
    const order = [...steps];
    [order[idx], order[to]] = [order[to], order[idx]];
    try {
      await api.post(`/sops/${sopId}/steps/reorder`, order.map((s) => s.id));
      await load();
    } catch (e) { toast.error(apiErrorMessage(e, "Reorder failed")); }
  };

  // Only marks the field dirty when the new value differs from the saved one.
  const setLocal = (key, val) => setDraft((d) => {
    const currentSaved = selected ? selected[key] : undefined;
    const next = { ...d };
    if (equal(val, currentSaved)) {
      delete next[key];
    } else {
      next[key] = val;
    }
    return next;
  });

  const kind = draft.kind ?? selected?.kind ?? "checkbox";
  const config = draft.config ?? selected?.config ?? {};

  return (
    <div data-testid="sop-step-editor">
      <div className="flex items-center justify-between mb-2">
        <Label className="label-eyebrow">Steps ({steps.length})</Label>
        <div className="flex gap-1">
          <Button size="sm" variant="outline" className="rounded-none text-primary border-primary/50" onClick={() => setShowAI(true)} data-testid="sop-ai-open-btn">
            <Sparkles className="w-3.5 h-3.5 mr-1" /> AI Generate
          </Button>
          <Button size="sm" variant="outline" className="rounded-none" onClick={() => addStep()} disabled={busy} data-testid="sop-add-step-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add step
          </Button>
        </div>
      </div>

      {steps.length === 0 ? (
        <div className="border border-dashed border-border p-6 space-y-4" data-testid="sop-empty-state">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 border border-primary/40 bg-primary/5 flex items-center justify-center shrink-0">
              <Sparkles className="w-4 h-4 text-primary" />
            </div>
            <div>
              <div className="font-semibold text-sm">No steps yet</div>
              <p className="text-xs text-muted-foreground mt-1">
                Add steps manually with the "Add step" button above, or let{" "}
                <button type="button" className="text-primary underline" onClick={() => setShowAI(true)}>AI generate</button>{" "}
                a step list from a prompt or voice note.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 pt-2 border-t border-border/40">
            <TipRow icon={ListChecks} label="Checkbox" text="Simple confirmations & sign-offs" />
            <TipRow icon={FileText} label="Form entry" text="Capture structured fields" />
            <TipRow icon={Upload} label="Upload" text="Attach files or deliverables" />
            <TipRow icon={Compass} label="Navigate in-app" text="Send the runner to a specific screen" />
            <TipRow icon={ExternalLink} label="External URL" text="Open third-party portal, auto-pops" />
            <TipRow icon={Film} label="Video" text="Embed Loom / YouTube / Vimeo" />
            <TipRow icon={MonitorPlay} label="Embed" text="Sheets, Slides, Notion — any iframe URL" />
            <TipRow icon={Clock} label="Advanced" text="Chain 30/60/90-day follow-ups with Depends + Wait days" />
          </div>
        </div>
      ) : (
        <div className="border border-border divide-y divide-border" data-testid="sop-step-list">
          {steps.map((s, i) => {
            const Icon = kindIcon(s.kind);
            const isOpen = s.id === selId;
            const draftedTitle = isOpen ? (draft.title ?? s.title) : s.title;
            const stepKind = isOpen ? (draft.kind ?? s.kind ?? "checkbox") : (s.kind ?? "checkbox");
            const stepConfig = isOpen ? (draft.config ?? s.config ?? {}) : (s.config ?? {});
            return (
              <div key={s.id} className="group bg-background" data-testid={`sop-step-item-${s.id}`}>
                {/* Accordion header — full-width, single column */}
                <div
                  className={`flex items-start gap-2 p-3 cursor-pointer transition ${isOpen ? "bg-primary/5 border-l-4 border-primary" : "hover:bg-muted/30 border-l-4 border-transparent"}`}
                  onClick={() => selectStep(isOpen ? null : s.id)}
                >
                  <button
                    type="button"
                    className={`w-5 h-5 shrink-0 flex items-center justify-center transition ${isOpen ? "rotate-90" : ""}`}
                    aria-label={isOpen ? "Collapse step" : "Expand step"}
                  >
                    <ChevronRight className="w-3.5 h-3.5 opacity-70" />
                  </button>
                  <span className="text-[10px] font-mono w-5 shrink-0 pt-0.5 text-muted-foreground">{i + 1}</span>
                  <Icon className="w-3.5 h-3.5 shrink-0 mt-0.5 opacity-70" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium break-words">{draftedTitle || <span className="text-muted-foreground italic">Untitled step</span>}</div>
                    <div className="text-[10px] text-muted-foreground uppercase tracking-wider mt-0.5">
                      {(KINDS.find((k) => k.v === stepKind) || {}).label || stepKind}
                      {s.is_required === false && " · optional"}
                      {s.depends_on_step_id && " · depends"}
                    </div>
                  </div>
                  <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition" onClick={(e) => e.stopPropagation()}>
                    <button type="button" className="p-1 disabled:opacity-30" disabled={i === 0} onClick={() => move(i, -1)} data-testid={`sop-step-up-${s.id}`}><ChevronUp className="w-3.5 h-3.5" /></button>
                    <button type="button" className="p-1 disabled:opacity-30" disabled={i === steps.length - 1} onClick={() => move(i, 1)} data-testid={`sop-step-down-${s.id}`}><ChevronDown className="w-3.5 h-3.5" /></button>
                    <button
                      type="button"
                      className="p-1 hover:text-primary"
                      onClick={() => addStep({ insertAfter: s.id })}
                      disabled={busy}
                      data-testid={`sop-add-below-${s.id}`}
                      title="Add step below"
                    >
                      <PlusSquare className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                {/* Accordion body — the step editor itself */}
                {isOpen && selected && (
                  <div className="p-4 pt-2 border-t border-border/60 bg-muted/10 space-y-3" data-testid="sop-step-detail">
                    <div className="flex items-center gap-2">
                      <Input
                        value={draft.title ?? selected.title}
                        onChange={(e) => setLocal("title", e.target.value)}
                        className="h-9 rounded-none text-sm flex-1 font-semibold"
                        data-testid={`sop-step-title-${selected.id}`}
                        placeholder="Step title (this is what appears in the runner and on the accordion header)"
                      />
                      <Button size="sm" variant="ghost" className="rounded-none h-9 w-9 p-0 text-red-600" onClick={() => delStep(selected.id)} data-testid={`sop-step-del-${selected.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <Select value={stepKind} onValueChange={(v) => setLocal("kind", v)}>
                        <SelectTrigger className="h-8 w-56 rounded-none text-xs" data-testid={`sop-step-kind-${selected.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {KINDS.map((k) => <SelectItem key={k.v} value={k.v}>{k.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <label className="text-xs inline-flex items-center gap-1.5">
                        <input type="checkbox" checked={(draft.is_required ?? selected.is_required) !== false} onChange={(e) => setLocal("is_required", e.target.checked)} data-testid={`sop-step-req-${selected.id}`} />
                        Required
                      </label>
                    </div>
                    <div>
                      <Label className="label-eyebrow text-[10px] mb-1 block">Instructions (rich text)</Label>
                      <SmartRichText
                        key={selected.id}
                        value={draft.description ?? selected.description ?? ""}
                        onChange={(html) => setLocal("description", html)}
                        placeholder="Instructions the runner should read while executing this step. Type `/` for headings, lists, images. `/ai` to polish."
                        mediaMode="upload"
                        workspaceId={activeWorkspaceId}
                        minRows={4}
                        maxHeight={400}
                        testid={`sop-step-desc-${selected.id}`}
                      />
                    </div>
                    <KindConfig kind={stepKind} config={stepConfig} onChange={(c) => setLocal("config", c)} testKey={selected.id} />
                    {KIND_TIPS[stepKind] && (
                      <div className="border border-amber-500/30 bg-amber-500/5 p-3 space-y-1" data-testid={`sop-step-tips-${selected.id}`}>
                        <div className="text-[10px] uppercase tracking-widest text-amber-700 font-semibold inline-flex items-center gap-1">
                          <Sparkles className="w-3 h-3" /> Tips for this step type
                        </div>
                        <ul className="space-y-0.5 text-xs">
                          {KIND_TIPS[stepKind].map((t, ti) => (
                            <li key={ti} className="flex gap-1.5">
                              <span className="text-amber-600 shrink-0">•</span>
                              <span className="text-muted-foreground">{t}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Advanced */}
                    <div className="border-t border-border pt-2">
                      <button
                        type="button"
                        className="text-[11px] uppercase tracking-wide text-muted-foreground hover:text-primary flex items-center gap-1"
                        onClick={() => setShowAdvanced((v) => !v)}
                        data-testid={`sop-step-advanced-toggle-${selected.id}`}
                      >
                        <ChevronDown className={`w-3 h-3 transition-transform ${showAdvanced ? "" : "-rotate-90"}`} />
                        Advanced · Dependencies & scheduling
                      </button>
                      {showAdvanced && (
                        <div className="mt-2 space-y-2 p-2 bg-muted/10">
                          <div>
                            <Label className="text-[10px] mb-1 flex items-center gap-1"><Link2 className="w-3 h-3" /> Depends on step</Label>
                            <Select
                              value={draft.depends_on_step_id ?? selected.depends_on_step_id ?? "__none__"}
                              onValueChange={(v) => setLocal("depends_on_step_id", v === "__none__" ? null : v)}
                            >
                              <SelectTrigger className="h-8 rounded-none text-xs" data-testid={`sop-step-dep-${selected.id}`}>
                                <SelectValue placeholder="No dependency" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__none__">No dependency (starts anytime)</SelectItem>
                                {steps.filter((x) => x.id !== selected.id).map((x, idx) => (
                                  <SelectItem key={x.id} value={x.id}>{idx + 1}. {x.title}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-[10px] mb-1 flex items-center gap-1"><Clock className="w-3 h-3" /> Wait after dependency (days)</Label>
                            <Input
                              type="number"
                              min="0"
                              step="0.5"
                              value={draft.wait_days ?? selected.wait_days ?? ""}
                              onChange={(e) => setLocal("wait_days", e.target.value === "" ? null : Number(e.target.value))}
                              placeholder="e.g. 30 for a 30-day follow-up"
                              className="h-8 rounded-none text-xs w-40"
                              data-testid={`sop-step-wait-${selected.id}`}
                            />
                            <p className="text-[10px] text-muted-foreground mt-1">Combine with "Depends on step" to build 30/60/90-day review flows.</p>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-2 justify-end">
                      {dirty && (
                        <>
                          <span className="text-[10px] text-amber-600 mr-auto">Unsaved changes</span>
                          <Button size="sm" variant="ghost" className="rounded-none h-8 text-xs" onClick={() => setDraft({})} data-testid={`sop-step-discard-${selected.id}`}>Discard</Button>
                          <Button size="sm" className="rounded-none h-8 text-xs" onClick={saveStep} data-testid={`sop-step-save-${selected.id}`}>
                            Save changes
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showAI && (
        <SOPAIGenerator
          sopId={sopId}
          wsId={activeWorkspaceId}
          sopTitle={sopTitle}
          onClose={() => setShowAI(false)}
          onDone={async () => { setShowAI(false); await load(false); }}
        />
      )}
    </div>
  );
}

function KindConfig({ kind, config, onChange, testKey }) {
  const setK = (k, v) => onChange({ ...(config || {}), [k]: v });
  if (kind === "navigate") {
    return (
      <div className="space-y-2">
        <RoutePicker value={config.route || ""} onChange={(v) => setK("route", v)} testid={`sop-cfg-route-${testKey}`} />
        <Input placeholder="Button label (optional — defaults to 'Go to page')" value={config.label || ""} onChange={(e) => setK("label", e.target.value)} className="h-8 rounded-none text-xs" data-testid={`sop-cfg-label-${testKey}`} />
      </div>
    );
  }
  if (kind === "external_url") {
    return (
      <div className="space-y-2">
        <Input
          placeholder="https://example.com/…"
          value={config.url || ""}
          onChange={(e) => setK("url", e.target.value)}
          className="h-8 rounded-none text-xs"
          data-testid={`sop-cfg-external-url-${testKey}`}
        />
        <div className="flex items-center gap-3 flex-wrap">
          <Input
            placeholder="Button label (optional)"
            value={config.label || ""}
            onChange={(e) => setK("label", e.target.value)}
            className="h-8 rounded-none text-xs flex-1"
            data-testid={`sop-cfg-external-label-${testKey}`}
          />
          <label className="text-[11px] inline-flex items-center gap-1.5 whitespace-nowrap">
            <input
              type="checkbox"
              checked={config.auto_open !== false}
              onChange={(e) => setK("auto_open", e.target.checked)}
              data-testid={`sop-cfg-external-autoopen-${testKey}`}
            />
            Auto-open when step starts
          </label>
        </div>
        <p className="text-[10px] text-muted-foreground">
          Opens a new browser tab. Auto-open is on by default and will pop the URL as soon as the runner reaches this step.
        </p>
      </div>
    );
  }
  if (kind === "embed") {
    return (
      <div className="flex gap-2">
        <Input placeholder="https://docs.google.com/spreadsheets/... /embed" value={config.url || ""} onChange={(e) => setK("url", e.target.value)} className="h-8 rounded-none text-xs flex-1" data-testid={`sop-cfg-embed-url-${testKey}`} />
        <Input type="number" placeholder="Height px (default 320)" value={config.height || ""} onChange={(e) => setK("height", Number(e.target.value) || undefined)} className="h-8 w-32 rounded-none text-xs" data-testid={`sop-cfg-embed-h-${testKey}`} />
      </div>
    );
  }
  if (kind === "video") {
    return (
      <div className="flex gap-2">
        <Input placeholder="YouTube / Vimeo / Loom / .mp4 URL" value={config.url || ""} onChange={(e) => setK("url", e.target.value)} className="h-8 rounded-none text-xs flex-1" data-testid={`sop-cfg-video-url-${testKey}`} />
        <Select value={config.provider || "auto"} onValueChange={(v) => setK("provider", v === "auto" ? undefined : v)}>
          <SelectTrigger className="h-8 w-32 rounded-none text-xs" data-testid={`sop-cfg-video-provider-${testKey}`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="auto">Auto-detect</SelectItem>
            <SelectItem value="youtube">YouTube</SelectItem>
            <SelectItem value="vimeo">Vimeo</SelectItem>
            <SelectItem value="loom">Loom</SelectItem>
            <SelectItem value="file">Direct file</SelectItem>
          </SelectContent>
        </Select>
      </div>
    );
  }
  if (kind === "upload") {
    return (
      <div className="flex gap-2">
        <Input type="number" placeholder="Min files (default 1)" value={config.min_files || ""} onChange={(e) => setK("min_files", Number(e.target.value) || undefined)} className="h-8 w-40 rounded-none text-xs" data-testid={`sop-cfg-min-${testKey}`} />
        <Input placeholder="Accept (e.g. image/*,application/pdf)" value={config.accept || ""} onChange={(e) => setK("accept", e.target.value)} className="h-8 rounded-none text-xs flex-1" data-testid={`sop-cfg-accept-${testKey}`} />
      </div>
    );
  }
  if (kind === "form") {
    return (
      <div>
        <Textarea
          placeholder='JSON array of fields, e.g. [{"key":"name","label":"Name","type":"text","required":true}]'
          value={config.fields ? (typeof config.fields === "string" ? config.fields : JSON.stringify(config.fields, null, 2)) : ""}
          onChange={(e) => {
            try {
              const arr = e.target.value.trim() ? JSON.parse(e.target.value) : undefined;
              setK("fields", arr);
            } catch { setK("fields", e.target.value); /* keep raw to allow typing */ }
          }}
          className="min-h-[80px] text-[10px] font-mono rounded-none"
          data-testid={`sop-cfg-form-${testKey}`}
        />
        <p className="text-[10px] text-muted-foreground mt-0.5">Types: text · textarea · number · date. Required = true forces the field.</p>
      </div>
    );
  }
  return null; // checkbox has no extra config
}

function TipRow({ icon: Icon, label, text }) {
  return (
    <div className="flex items-center gap-2 text-xs" data-testid={`sop-tip-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <Icon className="w-3.5 h-3.5 opacity-60 shrink-0" />
      <span className="font-medium">{label}</span>
      <span className="text-muted-foreground">— {text}</span>
    </div>
  );
}
